import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  booleanAttribute,
} from "@angular/core";

import { TranslateService } from "@ngx-translate/core";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import {
  ProfilePromiseActionService,
  ProfileSettingKeys,
  SystemConfigPromiseService,
  VwapPromiseService,
} from "shared/cubiq-services";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { LoggerService } from "@bfx/logger";

import { BfxRiskTradeContextMenuService } from "app/bfx-blotter-risk-trade/services/bfx-risk-trade-contextmenu.service";
import { BfxRiskTradeFilterService } from "app/bfx-blotter-risk-trade/services/bfx-risk-trade-filter.service";
import { DealOrderTypeRenderer } from "app/bfx-blotter-trade/renderers/DealOrderTypeRenderer";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { BfxVwapTradeService } from "app/bfx-vwap/bfx-vwap-trade.service";
import { Observable, Subject, Subscription, debounce, timer } from "rxjs";
import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { BfxBlotterAction } from "shared/cubiq-services";

import { BfxAlgoRiskTradeBlotterService } from "app/bfx-algo-monitor/services/bfx-algo-risk-trade-blotter.service";
import { BfxBaseVwapBlotter } from "app/bfx-vwap/bfx-base-vwap-blotter";
import { BfxVwapService } from "app/bfx-vwap/bfx-vwap.service";
import * as BfxTradesHelper from "app/bfx-common/helpers/trades/BfxTradesHelper";
import { RowNode } from "ag-grid-community";
import { AlgoRiskTradeTableDefinitionFactory } from "shared/cubiq-services/model/AlgoRiskTradeTableDefinitionFactory";
import { Vwap } from "shared/cubiq-services/mediator/IVwapService";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
import { COLUMN_DIR } from "shared/cubiq-services/model/TradeTableDefinitionFactory.constants";

const logger = LoggerService.for(__dirname);

/** # of ms to debounce between applying any new trade filter */
const DEBOUNCE_FILTER_MS = 400;
const BLOTTER_PREFERENCES_KEY = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;

/**
 * Risk trade blotter that sits in the Algo Monitor, showing the trades for all selected algos. The selected
 * algos are specified using the input tradeOrderIds
 */
@Component({
  selector: "bfx-algo-risk-trade-blotter",
  templateUrl: "./html/bfx-algo-risk-trade-blotter.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BfxAlgoRiskTradeBlotterComponent extends BfxBaseVwapBlotter implements OnChanges {
  private selectionChangedSubject: Subject<number> = new Subject();
  /** Simple observable that publishes the number of selected rows */
  @Output()
  public selectionChanged: Observable<number> = this.selectionChangedSubject.asObservable();

  private vwapChangedSubject: Subject<string> = new Subject();
  /** Simple observable that publishes thecurrent VWAP */
  @Output()
  public vwapChanged: Observable<string> = this.vwapChangedSubject.asObservable();

  @Input({ transform: booleanAttribute }) enableToolbar = true;

  @Input() tradeOrderIds: string[] = [];

  /** Quick lookup set of all orders that we are displaying */
  private orderIdSet: Set<string>;

  /** Observable used to debounce data refreshes */
  private applyFilterSubject: Subject<void>;

  /** True if there are deals being loaded from the server for selected Ids */
  public loadingDeals = false;

  /** Last loading promise  */
  private loadingPromise: Promise<boolean>;

  public loadingLabel: string;

  private subscriptions: Subscription[] = [];
  private rendererSettings: { intentStylingEnabled: boolean };

  public frameworkComponents: any = {
    orderTypeRenderer: DealOrderTypeRenderer,
    directionRenderer: CommonIntentRenderer,
  };

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxAlgoRiskTradeBlotterService,
    _contextMenuService: BfxRiskTradeContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: AlgoRiskTradeTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxRiskTradeFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
    protected vwapTradeService: BfxVwapTradeService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      vwapInfo,
    );
    this.orderIdSet = new Set();

    // Prevent this VWAP from being sent out as VWAP notification, just local VWAP changes and
    // vwap changes broadcast as selection changes
    this.windowUndocked = true;

    this.profileActionService.getProfileConfig(BLOTTER_PREFERENCES_KEY).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });
    this.subscriptions.push(
      this.profileActionService
        .getProfileConfigUpdates(BLOTTER_PREFERENCES_KEY)
        .subscribe((profileValue) => {
          this.mapUserSettings(profileValue);
        }),
    );

    this.initialize();
  }

  private initialize() {
    this.loadingLabel = this.translationService.instant("algo_risk_blotter.loading_deals");
    this.applyFilterSubject = new Subject();
    // Update order state changes after quiet
    this.applyFilterSubject
      .pipe(debounce(() => timer(DEBOUNCE_FILTER_MS)))
      .subscribe(() => this.applyCurrentFilter());
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (let propName in changes) {
      switch (propName) {
        case "tradeOrderIds":
          this.updateTradeFilter();
          break;
      }
    }
  }

  getVWapService(): BfxVwapService {
    return this.vwapTradeService;
  }

  getVwapSource(): string {
    return "Algo Risk Trades";
  }

  getVWapUnits() {
    return "trades";
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Extract the styling settings and add to the grid context
   * @param profileValue
   */
  private mapUserSettings(profileValue: any) {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        const intentStylingEnabled = savedSettings.intentStylingEnabled === "y";
        if (intentStylingEnabled != this.rendererSettings.intentStylingEnabled) {
          this.rendererSettings.intentStylingEnabled = intentStylingEnabled;
          this.refreshRendererSettings();
        }
      }
    } catch (e) {
      logger.out.warn(`artb-c - settings error with value: 'profileValue' : ${e}`);
    }
  }

  /**
   * Refresh renderers that refernce changed context data
   */
  private refreshRendererSettings() {
    setTimeout(() => {
      logger.out.info("artb-c - Refreshing intent col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_DIR],
        force: true,
      });
    }, 100);
  }

  /**
   * Override to add in the contextual settings values for renderers
   * @returns
   */
  protected override initGridOptionsContext(): any {
    let retContext = super.initGridOptionsContext();
    // Add a blotterSettings - this is called from constructor so do init here rather than as property initialiser
    this.rendererSettings = { intentStylingEnabled: false };
    retContext.rendererSettings = this.rendererSettings;
    return retContext;
  }

  protected initializeColumnDefinitions(): void {
    super.initializeColumnDefinitions();
    this.gridOptions.rowClassRules = BfxTradesHelper.getRiskTradeRowStyles();
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ALGO_RISK_TRADE;
  }

  public onCellDoubleClicked(event) {
    if (typeof event?.node?.id === "string") {
      this.contextMenuService.fireActionEvent(
        BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS,
        this,
        [event.node.id],
        [event.node.data],
        null,
        null,
        null,
        event,
      );
    }
  }

  public getTabIdentifier(): string {
    return "BfxAlgoRiskBlotterComponent";
  }

  public override onSelectionChanged(): void {
    super.onSelectionChanged();

    this.selectionChangedSubject.next(this.gridOptions.api?.getSelectedNodes().length ?? 0);
  }

  /**
   * When trade order Ids have changed, wait for a period then
   * refreshed the trades
   */
  private updateTradeFilter() {
    if (Array.isArray(this.tradeOrderIds)) {
      this.orderIdSet = new Set(this.tradeOrderIds);
    } else {
      this.tradeOrderIds = [];
      this.orderIdSet = new Set();
    }
    this.applyFilterSubject.next();
  }

  /**
   * When the time has come to refresh the filter, ensure data service has the trades
   * for all the selected orders, and notify the blotter that the filter conditions have changed
   */
  private applyCurrentFilter() {
    const loadResult = this.service.loadTradesForOrders(this.tradeOrderIds);
    this.gridOptions.api.onFilterChanged();

    if (loadResult.hasMissingTrades) {
      this.loadingDeals = true;
      const loadingPromise = loadResult.loadTradesPromise;

      this.loadingPromise = loadingPromise;
      this.loadingPromise.then(
        (result) => this.finishLoadingTrades(loadingPromise, result),
        (error) => this.finishLoadingTrades(loadingPromise, error),
      );
    } else {
      this.loadingDeals = false;
      this.loadingPromise = null;
    }
  }

  /**
   * Check to ensure that the specified promise is last one to load trades. If so, then
   * remove the flag to get rid of the callout
   * @param loadingPromise
   * @param result
   */
  private finishLoadingTrades(loadingPromise: Promise<boolean>, result: any) {
    if (this.loadingPromise === loadingPromise) {
      // We may have kicked off a different load so the flag is tied to a different promise result now
      this.loadingDeals = false;
      this.loadingPromise = null;
    }
  }

  public override isExternalFilterPresent(): boolean {
    return true;
  }

  public override doesExternalFilterPass(node: RowNode): boolean {
    let algo = node.data as CubiqTS.OrderDataImpl;
    // MAy need to look at unselecting non-visible nodes?
    return this.orderIdSet.has(algo.orderId?.toString());
  }

  /**
   * Calculate VWAP from the state of the blotter - override to use selected DEals as
   * these may not be available to the Vwap service if loaded from historical request
   * @returns
   */
  protected override getVWAP(): Promise<Vwap> {
    let selectedDeals: Array<CubiqTS.DealImpl> = this.getSelectedDeals();
    return this.vwapTradeService.getPairsVwapforTrades(selectedDeals);
  }

  private getSelectedDeals(): Array<CubiqTS.DealImpl> {
    let retDeals = [];
    let selectedRows = this.gridOptions.api?.getSelectedNodes();
    selectedRows.forEach((selectedRow) => {
      retDeals.push(selectedRow.data);
    });

    return retDeals;
  }

  /**
   * As this nested blotter we do only push VWAP changes as events, nothing global
   * @param vwapString
   * @param source
   * @returns
   */
  public override publishVWAP(vwapString: string, source: string): void {
    if (vwapString === "") {
      return;
    }
    this.vwapChangedSubject.next(vwapString);
  }

  /**
   * Clear all selected rows
   */
  public clearSelection(): void {
    this.gridOptions?.api?.deselectAll();
  }
}
